import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-uppercase text-caption"
}
const _hoisted_2 = { class: "text-body-2" }
const _hoisted_3 = { class: "text-body-2" }
const _hoisted_4 = ["href"]
const _hoisted_5 = {
  key: 0,
  class: "text-body-2",
  "data-cy": "payment-matters"
}
const _hoisted_6 = ["aria-label"]
const _hoisted_7 = { class: "text-body-2 text-right" }
const _hoisted_8 = { class: "text-center" }
const _hoisted_9 = { class: "bg-grey-lighten-4" }
const _hoisted_10 = ["colspan"]
const _hoisted_11 = { class: "text-body-2 font-weight-bold text-grey-darken-3 text-right pa-3" }
const _hoisted_12 = { class: "sett-step-footer" }

import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import AlertBar from '@/components/AlertBar.vue';
import { useSettlementStore } from '@/modules/matters/settlement/stores/useSettlementStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import { summarizePaymentMatters } from '@/modules/matters/settlement/utils/paymentMatterNames';
import { formatCurrency, getShortDateString } from '@/services/utils';
import type {
  DisplayPaymentItem,
  MatterNameSummary,
} from '@/models/settlementTypes';

interface PaymentRow extends DisplayPaymentItem {
  matterSummary: MatterNameSummary | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SettlementPaymentStep',
  props: {
  domain: { type: String, default: '' },
  /** `settlementClientAccounting` flipper — gates the "Matters:" column. */
  clientAccountingEnabled: { type: Boolean, default: false },
},
  emits: ['go-back', 'step-completed'],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const settlementStore = useSettlementStore();
const { terms } = storeToRefs(useMeStore());
const matterTerm = computed(() => terms.value?.Matter || 'Matter');

const paymentRows = computed<PaymentRow[]>(() =>
  settlementStore.displayPayments.map((payment) => ({
    ...payment,
    matterSummary: summarizePaymentMatters(payment.invoices),
  })),
);

/** No row spans multiple matters — an empty column would be pure clutter. */
const showMattersColumn = computed<boolean>(
  () =>
    props.clientAccountingEnabled &&
    paymentRows.value.some((row) => row.matterSummary),
);

// Load fresh payment data when this step mounts (v-if on parent controls lifecycle)
onMounted(async () => {
  if (!settlementStore.settlementId) return;
  await settlementStore.loadPayments();
});

async function confirmPaymentMapping() {
  try {
    await settlementStore.confirmClientPayments();
    emit('step-completed');
  } catch {
    // Error handled by store
  }
}

/** DD-V4-10 hover, mirroring DD-V4-7's claimed-deposit copy. Undefined when the row is
 *  selectable — LlTooltip then renders the bare checkbox with no tooltip wrapper. */
function lockedHoverText(payment: DisplayPaymentItem): string | undefined {
  if (!payment.isLocked) return undefined;
  const matterName = payment.lockedByMatterName?.trim() || 'another matter';
  return (
    `You cannot select this payment because it is associated with a settlement for matter ${matterName}. `
    + 'If this is in error, please edit that settlement to exclude this payment.'
  );
}

/** Build the invoice PDF URL for a given timeSheetId. */
function getInvoicePdfUrl(timeSheetId: string): string {
  return `${props.domain}/v1/invoices/${timeSheetId}/pdf`;
}

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!
  const _component_v_table = _resolveComponent("v-table")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createBlock(_component_v_card, {
    flat: "",
    class: "border-0 !bg-[rgb(var(--v-theme-surface))]"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card_title, null, {
        default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
          _createTextVNode("Step 5: Map Prior Payments", -1)
        ]))]),
        _: 1
      }),
      _createVNode(_component_v_card_text, null, {
        default: _withCtx(() => [
          _cache[13] || (_cache[13] = _createElementVNode("div", { class: "text-body-2 text-grey-darken-1 mb-4" }, [
            _createTextVNode(" The following payments have been made on this matter. Payments are "),
            _createElementVNode("strong", null, "included"),
            _createTextVNode(" by default. Uncheck any payment that should "),
            _createElementVNode("strong", null, "not"),
            _createTextVNode(" appear on the settlement statement. A payment another matter's settlement already counts is shown greyed out and cannot be selected here. ")
          ], -1)),
          (_unref(settlementStore).payments.loading)
            ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                key: 0,
                type: "table-row@4",
                class: "mb-4"
              }))
            : (_unref(settlementStore).displayPayments.length > 0)
              ? (_openBlock(), _createBlock(_component_v_table, {
                  key: 1,
                  density: "compact",
                  class: "mb-2"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("thead", null, [
                      _createElementVNode("tr", null, [
                        _cache[2] || (_cache[2] = _createElementVNode("th", { class: "text-uppercase text-caption" }, "Date", -1)),
                        _cache[3] || (_cache[3] = _createElementVNode("th", { class: "text-uppercase text-caption" }, "Description", -1)),
                        (showMattersColumn.value)
                          ? (_openBlock(), _createElementBlock("th", _hoisted_1, _toDisplayString(matterTerm.value) + "s ", 1))
                          : _createCommentVNode("", true),
                        _cache[4] || (_cache[4] = _createElementVNode("th", { class: "text-uppercase text-caption text-right" }, "Amount", -1)),
                        _cache[5] || (_cache[5] = _createElementVNode("th", { class: "text-uppercase text-caption text-center w-[80px]" }, " Include? ", -1))
                      ])
                    ]),
                    _createElementVNode("tbody", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paymentRows.value, (payment) => {
                        return (_openBlock(), _createElementBlock("tr", {
                          key: payment.externalPaymentId,
                          class: _normalizeClass({ 'text-disabled': payment.isLocked })
                        }, [
                          _createElementVNode("td", _hoisted_2, _toDisplayString(_unref(getShortDateString)(payment.paymentDate)), 1),
                          _createElementVNode("td", _hoisted_3, [
                            _cache[7] || (_cache[7] = _createTextVNode(" Payment", -1)),
                            (payment.invoices && payment.invoices.length)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _cache[6] || (_cache[6] = _createTextVNode(" — ", -1)),
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(payment.invoices, (inv, i) => {
                                    return (_openBlock(), _createElementBlock(_Fragment, {
                                      key: inv.timeSheetId
                                    }, [
                                      (i > 0)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            _createTextVNode(", ")
                                          ], 64))
                                        : _createCommentVNode("", true),
                                      _createElementVNode("a", {
                                        href: getInvoicePdfUrl(inv.timeSheetId),
                                        target: "_blank",
                                        rel: "noopener noreferrer",
                                        class: "text-decoration-underline text-primary"
                                      }, "Inv #" + _toDisplayString(inv.invoiceNumber || inv.timeSheetId), 9, _hoisted_4)
                                    ], 64))
                                  }), 128))
                                ], 64))
                              : _createCommentVNode("", true)
                          ]),
                          (showMattersColumn.value)
                            ? (_openBlock(), _createElementBlock("td", _hoisted_5, [
                                (payment.matterSummary)
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                      _createTextVNode(_toDisplayString(payment.matterSummary.visible.join(', ')) + " ", 1),
                                      (payment.matterSummary.hiddenCount > 0)
                                        ? (_openBlock(), _createBlock(LlTooltip, {
                                            key: 0,
                                            text: payment.matterSummary.all.join(', '),
                                            location: "top",
                                            "max-width": "260"
                                          }, {
                                            activator: _withCtx(() => [
                                              _createElementVNode("span", {
                                                class: "ml-1 cursor-pointer text-grey-darken-1 text-decoration-underline",
                                                tabindex: "0",
                                                "aria-label": `${matterTerm.value}s: ${payment.matterSummary.all.join(', ')}`,
                                                "data-cy": "payment-matters-overflow"
                                              }, " +" + _toDisplayString(payment.matterSummary.hiddenCount) + " more ", 9, _hoisted_6)
                                            ]),
                                            _: 2
                                          }, 1032, ["text"]))
                                        : _createCommentVNode("", true)
                                    ], 64))
                                  : _createCommentVNode("", true)
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("td", _hoisted_7, _toDisplayString(_unref(formatCurrency)(payment.amount)), 1),
                          _createElementVNode("td", _hoisted_8, [
                            _createVNode(LlTooltip, {
                              location: "top",
                              "max-width": 300,
                              text: lockedHoverText(payment),
                              "data-cy": `payment-lock-${payment.externalPaymentId}`
                            }, {
                              activator: _withCtx(() => [
                                _createVNode(LlCheckbox, {
                                  "model-value": payment.isIncluded,
                                  disabled: payment.isLocked,
                                  "aria-label": `Include payment of ${_unref(formatCurrency)(payment.amount)} on ${_unref(getShortDateString)(payment.paymentDate)}`,
                                  "data-cy": `payment-include-${payment.externalPaymentId}`,
                                  class: "d-inline-flex",
                                  "onUpdate:modelValue": ($event: any) => (
                        _unref(settlementStore).togglePayment(payment.externalPaymentId)
                      )
                                }, null, 8, ["model-value", "disabled", "aria-label", "data-cy", "onUpdate:modelValue"])
                              ]),
                              _: 2
                            }, 1032, ["text", "data-cy"])
                          ])
                        ], 2))
                      }), 128))
                    ]),
                    _createElementVNode("tfoot", null, [
                      _createElementVNode("tr", _hoisted_9, [
                        _createElementVNode("td", {
                          colspan: showMattersColumn.value ? 3 : 2,
                          class: "text-body-2 font-weight-bold text-grey-darken-3 pa-3"
                        }, " Total Client Payments (included): ", 8, _hoisted_10),
                        _createElementVNode("td", _hoisted_11, _toDisplayString(_unref(formatCurrency)(_unref(settlementStore).clientPaidTotal)), 1),
                        _cache[8] || (_cache[8] = _createElementVNode("td", null, null, -1))
                      ])
                    ])
                  ]),
                  _: 1
                }))
              : (_openBlock(), _createBlock(AlertBar, {
                  key: 2,
                  type: "info",
                  density: "default",
                  class: "mb-4"
                }, {
                  default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                    _createTextVNode(" No non-trust payments found for this matter. ", -1)
                  ]))]),
                  _: 1
                })),
          (_unref(settlementStore).displayPayments.length > 0)
            ? (_openBlock(), _createBlock(AlertBar, {
                key: 3,
                type: "info",
                class: "mb-4"
              }, {
                default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                  _createElementVNode("span", { class: "text-body-2" }, [
                    _createElementVNode("strong", null, "Tip:"),
                    _createTextVNode(" Payments start checked (included) unless another matter's settlement already counts them. If you see a payment that doesn't belong on the statement — for example a post-settlement trust transfer — simply uncheck it. ")
                  ], -1)
                ]))]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_12, [
            _createVNode(LlSecondaryButton, {
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('go-back')))
            }, {
              default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                _createTextVNode(" ← Back ", -1)
              ]))]),
              _: 1
            }),
            _createVNode(LlSecondaryButton, {
              color: "primary",
              loading: _unref(settlementStore).saving,
              onClick: confirmPaymentMapping
            }, {
              default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                _createTextVNode(" Confirm Payment Mapping → ", -1)
              ]))]),
              _: 1
            }, 8, ["loading"])
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})